import { Fragment, useEffect, useMemo, useState } from 'react'
import useGeoFeatures from '../hooks/useGeoFeatures'
import './Sidebar.scss'
import { DeleteIcon, EditIcon, ExportSvg, GeoFenceIcon, TargetTrackIcon, UploadSvg } from '../Svgs'
import CustomToolTip from '../../CommonComponents/CustomToolTip'
import { checkCommonNameValidation, containsWhitespace, navigateTo, preventEnterKey, removeWhiteSpace } from '../../../utils/commonFunctions'
import { CircularProgress } from '@mui/material'
import EmptySection from './EmptySection'
import { isEqual } from 'lodash'
import GroupButtonList from '../../CommonComponents/GroupButtonList'
import { AcpLogo, BackSvg, LeftbarCloseSvg } from '../../CommonComponents/svgs/Svgs'
import { useNavigate } from 'react-router-dom'
import { downloadAsShapefile } from '../utils/utilities'

export const MenuList = [
    {
        name: 'Edit',
        Icon: <EditIcon />
    },
    {
        name: 'Delete',
        Icon: <DeleteIcon />
    },
    {
        name: 'Export',
        Icon: <ExportSvg />
    },
]

const Sidebar = () => {
    const navigate = useNavigate()
    const { rename, setRename, sidebarOpen, setSidebarOpen, geoFeaturesDetails, toggleViewMode, selectedFeature, setSelectedFeature, onMenuClick, renameFeature, actionFeature } = useGeoFeatures()
    const [openMenu, setOpenMenu] = useState<boolean | string>(false)
    const loading = useMemo(() => geoFeaturesDetails?.loading, [geoFeaturesDetails])
    const allFeatures = useMemo(() => geoFeaturesDetails?.allFeatures || [], [geoFeaturesDetails])
    const open = useMemo(() => sidebarOpen, [sidebarOpen])

    const navigateHome = () => {
        navigateTo(``, navigate)
        setSelectedFeature(null)
    }
    const onChangeName = (value: string) => {
        if (!rename || !checkCommonNameValidation(value, 26)) return
        else if (!containsWhitespace(value)) {
            setRename({ ...rename, name: value })
        }
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (event.target instanceof Element) {
                if (event.target.closest('.sidebar__sections__body__item__container')
                    || event.target.closest('.rename__check__button')) return
                setRename(null)
            }
        }
        document.addEventListener('click', handleClickOutside)
        return () => {
            document.removeEventListener('click', handleClickOutside)
        }
    }, [])


    return (
        <div id='left_sidebar' className={`sidebar__wrapper`}>
            <div id='sidebar_header' className='sidebar__header__container'>
                <button className='header__logo__container'
                    onClick={navigateHome}
                >
                    <AcpLogo />
                </button>
                <div className='header__details__container'>
                    <button className='header__back__button'
                        onClick={() => {
                            setSelectedFeature(null)
                            toggleViewMode()
                        }}
                    >
                        <BackSvg />
                    </button>
                </div>
            </div>
            <div className={`sidebar__container ${open && 'active'}`} data-testid='lsidebar'>
                <LeftbarCloseSvg active={open} onClick={() => setSidebarOpen(!open)} />
                <div id='feature_section' className='sidebar__container__sections'>
                    <div className='sidebar__sections__header'>
                        <div className='sidebar__section__header__title'>GEOFEATURES <span className='opacity-75'>({allFeatures?.length})</span></div>
                        {allFeatures?.length > 0 &&
                            <CustomToolTip title='Export All' placement='bottom'>
                                <button
                                    className={`section__header__button`}
                                    onClick={() => { downloadAsShapefile(allFeatures) }}
                                >
                                    <UploadSvg />
                                </button>
                            </CustomToolTip>
                        }
                    </div>
                    <div className='sidebar__sections__body__container'>
                        {!loading && allFeatures?.length > 0 && allFeatures.map((feature) => {
                            const uniqueId = feature.areaOfInterestId
                            const renaming = rename?.uniqueId === uniqueId
                            const selected = uniqueId === selectedFeature?.uniqueId
                            const name = feature.name
                            return (
                                <button
                                    key={uniqueId}
                                    className={`sidebar__sections__body__item__container 
                                        ${(openMenu === uniqueId || renaming) && 'show__menu'}
                                        ${renaming && 'status__renaming'} ${selected && 'status__selected'}
                                    `}
                                >
                                    <div className={`icon__container ${feature?.type === 'Target Track' && 'target__icon'}`}>
                                        {feature?.type === 'Geofence' && <GeoFenceIcon />}
                                        {feature?.type === 'Target Track' && <TargetTrackIcon />}
                                    </div>
                                    <div className='text__container'
                                        onDoubleClick={() => {
                                            if (renaming) return
                                            setRename({
                                                name: name,
                                                uniqueId: uniqueId,
                                                loading: false,
                                            })
                                        }}
                                    >
                                        {!renaming &&
                                            <CustomToolTip title={name} placement='top-start'>
                                                {name}
                                            </CustomToolTip>
                                        }
                                        {renaming &&
                                            <input
                                                className='text__input'
                                                autoFocus
                                                type='text'
                                                value={rename?.name}
                                                onKeyDown={preventEnterKey}
                                                onChange={(e) => onChangeName(removeWhiteSpace(e.target.value))}
                                            />
                                        }
                                    </div>
                                    {!renaming &&
                                        <div className='button__container'>
                                            <GroupButtonList
                                                id={uniqueId}
                                                open={openMenu}
                                                disabled={loading}
                                                toggle={(value) => {
                                                    setRename(null)
                                                    setOpenMenu(value)
                                                }}
                                                buttons={MenuList}
                                                onClick={(btn) => {
                                                    onMenuClick(btn, feature)
                                                }}
                                            >
                                                <i className='fe fe-more-vertical ' />
                                            </GroupButtonList>
                                        </div>
                                    }
                                    {renaming &&
                                        <div className='button__container'>
                                            {!rename?.loading &&
                                                <Fragment>
                                                    <CustomToolTip title='Close' placement='top'>
                                                        <button className='rename__close__button'
                                                            disabled={rename?.loading}
                                                            onClick={() => setRename(null)}
                                                        >
                                                            <i className='fe fe-x' />
                                                        </button>
                                                    </CustomToolTip>
                                                    <CustomToolTip title='Save' placement='top'>
                                                        <button className='rename__check__button'
                                                            disabled={rename?.loading || rename?.name === '' || isEqual(rename?.name!, feature?.name)}
                                                            onClick={renameFeature}
                                                        >
                                                            <i className='fe fe-check' />
                                                        </button>
                                                    </CustomToolTip>
                                                </Fragment>
                                            }
                                            {rename?.loading && <CircularProgress color='inherit' size={14} />}
                                        </div>
                                    }
                                </button>
                            )
                        })}
                        {(loading || !allFeatures?.length) && <EmptySection loading={loading} message='Create GeoFeature' onClick={() => actionFeature('Create')} />}
                    </div>
                </div>

            </div>
        </div>
    )
}

export default Sidebar